import React, { useState } from "react";
import "./Home.css";

/* MUI */
import { Container, Grid, Button, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

/* Images */
import logoImg from "./assets/logo.png";
import heroImg from "./assets/kaaba_hero.png";
import aboutImg from "./assets/about.png";
import mapImg from "./assets/map.png";
import guide1Img from "./assets/guides.png";
import guide2Img from "./assets/guides2.png";
import communityChatImg from "./assets/community-groups.png";
import communityFeedImg from "./assets/community.png";
import service1Img from "./assets/service-1.png";
import service2Img from "./assets/service-2.png";
import service3Img from "./assets/service-1.png";
import globeImg from "./assets/globe.png";
import { useNavigate } from "react-router-dom";

export default function Home() {
  const [open, setOpen] = useState(false);
  const navigateTo = useNavigate();

  const goToSignup = () => {
    setOpen(false);
    navigateTo("/signup");
  };

  const features = [
    "Track your Hajj/Umrah group in real time",
    "Instantly locate lost members through GPS",
    "Share your live position with the group leader",
    "Send emergency alerts or messages instantly",
    "Navigate to key holy sites with guided maps",
  ];

  return (
    <div className="lp-page">
      {/* ===== NAV ===== */}
      <header className="lp-nav">
        <Container maxWidth="lg" className="lp-navInner">
          <a href="#top" className="lp-brand">
            <img src={logoImg} alt="Tawaf" />
          </a>

          <nav className={`lp-links ${open ? "is-open" : ""}`}>
            <a href="#about">About</a>
            <a href="#lost">Location</a>
            <a href="#guide">Guide</a>
            <a href="#community">Community</a>
            <a href="#services">Services</a>
            <a href="#contact">Contact</a>
            <Button
              className="lp-cta"
              variant="contained"
              color="primary"
              onClick={goToSignup}
            >
              Join Now
            </Button>
          </nav>

          <IconButton
            className="lp-burger"
            onClick={() => setOpen((v) => !v)}
            size="large"
            aria-label="Toggle navigation"
          >
            {open ? <CloseRoundedIcon /> : <MenuIcon />}
          </IconButton>
        </Container>
      </header>

      {/* ===== HERO ===== */}
      <section
        id="top"
        className="lp-hero"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <Container maxWidth="lg" className="lp-heroInner">
          <h1>
            Experience a Peaceful and Connected Journey
            <br />
            Stay Linked with Your Group Every Step of the Way
            <br />
            <span>Powered by Tawaf Platform</span>
          </h1>
          <Button
            className="lp-cta"
            variant="contained"
            color="primary"
            size="large"
            onClick={goToSignup}
          >
            Join Now
          </Button>
        </Container>
        <div className="lp-heroFade" />
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="lp-section">
        <Container maxWidth="lg">
          <h2 className="lp-h2">About Tawaf</h2>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <p className="lp-text">
                Tawaf is a digital companion designed to simplify and enhance
                your pilgrimage experience. Our mission is to ensure safety,
                communication, and guidance for every pilgrim — from departure
                to return.
              </p>
              <p className="lp-text">
                With Tawaf, you can stay connected with your group, track your
                companions in crowded areas, and receive essential Hajj or Umrah
                instructions — all from one reliable app. We’re here to make
                your spiritual journey smoother, safer, and stress-free.
              </p>
            </Grid>
            <Grid item xs={12} md={5}>
              <div className="lp-figureCard">
                <img src={aboutImg} alt="About Tawaf" />
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ===== LOST LOCATION ===== */}
      <section id="lost" className="lp-section">
        <Container maxWidth="lg">
          <h2 className="lp-h2">Locate Group Members</h2>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <ul className="lp-featureList">
                {features.map((f) => (
                  <li key={f} className="lp-featureItem">
                    <span className="lp-bullet">
                      <CheckCircleRoundedIcon />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </Grid>
            <Grid item xs={12} md={7}>
              <div className="lp-mapCard">
                <img src={mapImg} alt="Map" />
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ===== GUIDE ===== */}
      <section id="guide" className="lp-section">
        <Container maxWidth="lg">
          <h2 className="lp-h2">Pilgrimage Guide</h2>
          <p className="lp-sub">
            Access authentic guidance, verified rituals, and step-by-step
            checklists to complete your pilgrimage correctly.
          </p>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <article className="lp-miniCard">
                <img src={guide1Img} alt="Travel Preparation" />
                <div className="lp-miniBody">
                  <h3>Before You Depart</h3>
                  <p>
                    Learn what to pack, essential documents to carry, and how to
                    prepare spiritually and physically for the journey.
                  </p>
                  <button className="lp-link">Read More</button>
                </div>
              </article>
            </Grid>
            <Grid item xs={12} md={6}>
              <article className="lp-miniCard">
                <img src={guide2Img} alt="Performing Tawaf" />
                <div className="lp-miniBody">
                  <h3>Performing Tawaf</h3>
                  <p>
                    Step-by-step guidance to perform Tawaf with peace of mind.
                    Includes Duas, Sunnah tips, and safety reminders.
                  </p>
                  <button className="lp-link">Read More</button>
                </div>
              </article>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ===== COMMUNITY ===== */}
      <section id="community" className="lp-section">
        <Container maxWidth="lg">
          <h2 className="lp-h2">Join the Pilgrim Community</h2>
          <p className="lp-sub">
            Connect with fellow travelers, share experiences, and get real-time
            support during your journey.
          </p>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <div className="lp-mediaCard">
                <img src={communityChatImg} alt="Community Chat" />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="lp-mediaCard">
                <img src={communityFeedImg} alt="Community Feed" />
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="lp-section">
        <Container maxWidth="lg">
          <h2 className="lp-h2">Our Services</h2>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <div className="lp-serviceCard">
                <img src={service1Img} alt="Transportation" />
                <div className="lp-serviceBody">
                  <h4>Smart Transportation</h4>
                  <p>
                    Book trusted Hajj and Umrah transfers, including pickups
                    between Makkah, Madinah, and airports.
                  </p>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <div className="lp-serviceCard">
                <img src={service2Img} alt="Group Tools" />
                <div className="lp-serviceBody">
                  <h4>Group Management</h4>
                  <p>
                    Manage your group, track members, and broadcast important
                    announcements in real time.
                  </p>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <div className="lp-serviceCard">
                <img src={service3Img} alt="Travel Assistance" />
                <div className="lp-serviceBody">
                  <h4>Travel Assistance</h4>
                  <p>
                    Get emergency help, support for lost members, and access to
                    verified local service providers.
                  </p>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="lp-section lp-contact">
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <div className="lp-globe">
                <img src={globeImg} alt="Global" />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="lp-formCard">
                <h3>Contact Us</h3>
                <form
                  className="lp-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    window.alert("Thanks! We’ll contact you soon.");
                  }}
                >
                  <input type="email" placeholder="Your Email" required />
                  <input type="text" placeholder="Your Name" required />
                  <textarea placeholder="Message" rows={4} />
                  <Button type="submit" variant="contained" className="lp-cta">
                    Send
                  </Button>
                </form>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="lp-footer">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <div className="lp-footBrand">
                <img src={logoImg} alt="Tawaf" />
                <p>
                  Tawaf is your all-in-one Hajj and Umrah companion — helping
                  you navigate, connect, and experience peace throughout your
                  pilgrimage.
                </p>
                <button className="lp-link small">Learn More</button>
              </div>
            </Grid>
            <Grid item xs={6} md={2}>
              <h5>Quick Links</h5>
              <ul className="lp-list">
                <li><a href="#top">Home</a></li>
                <li><a href="#guide">Guides</a></li>
                <li><a href="#community">Community</a></li>
                <li><a href="#services">Services</a></li>
              </ul>
            </Grid>
            <Grid item xs={12} md={3}>
              <h5>Subscribe</h5>
              <form
                className="lp-subscribe"
                onSubmit={(e) => {
                  e.preventDefault();
                  window.alert("Subscribed!");
                }}
              >
                <input type="email" placeholder="Enter your email" required />
                <Button variant="contained" className="lp-cta">Go</Button>
              </form>
              <h5 className="lp-follow">Follow Us</h5>
              <div className="lp-social">
                <a href="#">Facebook</a>
                <a href="#">Instagram</a>
                <a href="#">Twitter</a>
              </div>
            </Grid>
          </Grid>

          <div className="lp-copy">
            Copyright © {new Date().getFullYear()} Tawaf —
            All Rights Reserved <span className="lp-heart">❤</span>
          </div>
        </Container>
      </footer>
    </div>
  );
}
